import { FC, useState } from 'react';
import { Button, Menu, MenuItem, Switch } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ArrowDropDown } from '@mui/icons-material';
import { Alert } from '@mui/material';
import { Api } from 'further-types/eis-wizard';
import NotifyInvestorsTooltip from 'components/NotifyInvestorsTooltip';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

const useStyles = makeStyles()(() => ({
  buttonRow: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  notifyButton: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

type FormDataType = Pick<
  Api.MutateEisWizardRequest,
  | 'companyId'
  | 'shareAllocationUploadId'
  | 'notifyInvestors'
  | 'createdWithoutCertificates'
>;

type Props = {
  activeStep: number;
  handleBack: () => void;
  handleNext: () => void;
  handleCreateWithoutCertificates: (investmentType: string) => void;
  data: FormDataType;
  setData: (data: unknown) => void;
  isEditPage: boolean;
  isFinalStep: boolean;
};

const FormActionButtons: FC<Props> = ({
  activeStep,
  handleBack,
  handleNext,
  handleCreateWithoutCertificates,
  data,
  setData,
  isEditPage,
  isFinalStep,
}) => {
  const { classes } = useStyles();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [canCreateWithoutCertificates] = useHasPermissions([
    RolePermission.CreateSeisWithoutCertificates,
  ]);

  if (data.createdWithoutCertificates) {
    return (
      <div className={classes.buttonRow}>
        <Button color="primary" variant="outlined" onClick={handleBack}>
          Cancel
        </Button>
        <Alert severity="info">
          This allocation has been completed without generating certificates.
        </Alert>
      </div>
    );
  }

  return (
    <div className={classes.buttonRow}>
      <Button color="primary" variant="outlined" onClick={handleBack}>
        {activeStep === 0 ? 'Cancel' : 'Back'}
      </Button>
      <div className={classes.notifyButton}>
        {activeStep === 1 && !isEditPage && (
          <NotifyInvestorsTooltip title="If toggled, the system will send an email to each applicable investor notifying them that a new EIS certificate is available to view and download on their investor portal.">
            <Switch
              checked={data?.notifyInvestors}
              onChange={(event: any) =>
                setData({
                  ...data,
                  notifyInvestors: event.target.checked,
                })
              }
            />
          </NotifyInvestorsTooltip>
        )}

        {canCreateWithoutCertificates && activeStep === 0 && !isEditPage && (
          <>
            <Button
              color="primary"
              variant="outlined"
              aria-controls="simple-menu"
              aria-haspopup="true"
              endIcon={<ArrowDropDown />}
              //@ts-expect-error
              onClick={(event) => setMenuAnchorEl(event.currentTarget)}
              disabled={!data.companyId || !data.shareAllocationUploadId}
              style={{ marginRight: '0.5rem' }}
            >
              Complete without certificates
            </Button>
            <Menu
              id="simple-menu"
              anchorEl={menuAnchorEl}
              keepMounted
              open={Boolean(menuAnchorEl)}
              onClose={() => setMenuAnchorEl(null)}
            >
              <MenuItem onClick={() => handleCreateWithoutCertificates('EIS')}>
                EIS subscription
              </MenuItem>
              <MenuItem onClick={() => handleCreateWithoutCertificates('SEIS')}>
                SEIS subscription
              </MenuItem>
            </Menu>
          </>
        )}

        <Button
          color="primary"
          variant="contained"
          onClick={handleNext}
          disabled={!data.companyId || !data.shareAllocationUploadId}
        >
          {isFinalStep ? 'Submit' : 'Next'}
        </Button>
      </div>
    </div>
  );
};

export default FormActionButtons;
